import { TagadaError } from '../errors.js';
import type { HttpClient } from '../http.js';
import type {
  CheckoutSession,
  FunnelNavigateInput,
  FunnelNavigateResult,
} from '../types.js';
import type { CheckoutModule } from './checkout.js';

/**
 * Initialize a funnel session from the SDK's current cart and produce a
 * redirect URL into a funnel step.
 *
 * Mirrors the WooCommerce checkout plugin
 * (`public/assets/php/woocommerce/tagada-checkout-plugin.php`):
 *
 *  1. `POST /api/v1/cms/session/anonymous` — public, returns a CMS token.
 *  2. `POST /api/v1/cms/session/v2/init`   — uses the token, returns a CMS customer id.
 *  3. `POST /api/v1/funnel/initialize`     — creates the funnel session.
 *  4. `POST /api/v1/funnel/navigate`       — fires an `INIT_CHECKOUT` event and returns `{ url }`.
 *
 * Steps 1–3 are cached on the module instance, so repeat `navigate()` calls in
 * the same page only fire step 4.
 *
 * **Prerequisite**: the SDK must have a loaded `CheckoutSession`
 * (`sdk.checkout.loadSession(...)`). The cart is sourced from `session.items`,
 * the currency from `session.currency`, the customer email from
 * `session.customer.email`, and the promotion code from `session.promotionCode`.
 */
export class FunnelModule {
  private _cmsToken: string | undefined;
  private _cmsSessionId: string | undefined;
  private _cmsCustomerId: string | undefined;

  constructor(
    private readonly http: HttpClient,
    private readonly storeId: string,
    private readonly checkout: CheckoutModule,
    private readonly accountId: string | undefined,
  ) {}

  async navigate(input: FunnelNavigateInput): Promise<FunnelNavigateResult> {
    const session = this.checkout.getCurrentSession();
    if (!session) {
      throw new TagadaError(
        'No checkout session loaded. Call sdk.checkout.loadSession(...) before sdk.funnel.navigate(...).',
        'no_cart_loaded',
      );
    }

    const accountId = input.accountId ?? this.accountId;
    if (!accountId) {
      throw new TagadaError(
        'accountId is required for funnel.navigate(). Set it on createHeadlessClient({ accountId }) or pass it per call.',
        'missing_account_id',
      );
    }

    const currentUrl =
      input.currentUrl ?? (typeof globalThis !== 'undefined' ? globalThis.location?.href : undefined);

    await this.ensureCmsSession(session, accountId, input);

    const funnelSessionId = await this.initializeFunnel({
      funnelId: input.funnelId,
      stepId: input.stepId,
      accountId,
      currentUrl,
    });

    const url = await this.runNavigate({
      funnelSessionId,
      stepId: input.stepId,
      session,
      input,
      currentUrl,
    });

    return { url, funnelSessionId };
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private async ensureCmsSession(
    session: CheckoutSession,
    accountId: string,
    input: FunnelNavigateInput,
  ): Promise<void> {
    if (this._cmsToken && this._cmsSessionId && this._cmsCustomerId) return;

    const anon = await this.http.post<{ token: string; sessionId: string }>(
      '/api/v1/cms/session/anonymous',
      { storeId: this.storeId, role: 'anonymous', draft: false },
    );
    this._cmsToken = anon.token;
    this._cmsSessionId = anon.sessionId;

    const init = await this.http.post<{ customer: { id: string } }>(
      '/api/v1/cms/session/v2/init',
      {
        storeId: this.storeId,
        accountId,
        customerId: 'anon_placeholder',
        browserLocale: input.customer?.locale ?? defaultLocale(),
        userAgent: defaultUserAgent(),
        queryCurrency: input.customer?.currency ?? session.currency,
        queryLocale: input.customer?.locale,
      },
      { token: this._cmsToken },
    );

    this._cmsCustomerId = init.customer.id;
  }

  private async initializeFunnel(args: {
    funnelId: string;
    stepId: string;
    accountId: string;
    currentUrl: string | undefined;
  }): Promise<string> {
    const res = await this.http.post<{ context: { sessionId: string } }>(
      '/api/v1/funnel/initialize',
      {
        cmsSession: {
          customerId: this._cmsCustomerId,
          sessionId: this._cmsSessionId,
          storeId: this.storeId,
          accountId: args.accountId,
        },
        funnelId: args.funnelId,
        entryStepId: args.stepId,
        funnelStepId: args.stepId,
        currentUrl: args.currentUrl,
        draft: false,
      },
      { token: this._cmsToken },
    );
    return res.context.sessionId;
  }

  private async runNavigate(args: {
    funnelSessionId: string;
    stepId: string;
    session: CheckoutSession;
    input: FunnelNavigateInput;
    currentUrl: string | undefined;
  }): Promise<string> {
    const { funnelSessionId, stepId, session, input, currentUrl } = args;

    const lineItems = session.items.map((i) => ({
      variantId: i.variantId,
      priceId: i.priceId,
      quantity: i.quantity,
    }));

    const discountCodes =
      input.discountCodes ??
      (session.promotionCode ? [session.promotionCode] : undefined);

    const customer = {
      email: input.customer?.email ?? session.customer?.email,
      currency: input.customer?.currency ?? session.currency,
      locale: input.customer?.locale,
    };

    const res = await this.http.post<{ result: { url: string } }>(
      '/api/v1/funnel/navigate',
      {
        sessionId: funnelSessionId,
        currentUrl,
        funnelStepId: stepId,
        fireAndForget: false,
        event: {
          type: 'INIT_CHECKOUT',
          data: {
            checkoutInit: {
              cartToken: session.id,
              lineItems,
              customer,
              returnUrl: input.returnUrl,
              storeId: this.storeId,
              metadata: input.metadata,
              discountCodes,
              // Pins the session to this exact shipping amount when the caller's
              // storefront already settled one (#TAG-1785). Left undefined when
              // absent, so it is omitted from the JSON body and the platform
              // auto-selects a rate exactly as before.
              externalShipping: input.externalShipping,
            },
          },
        },
      },
      { token: this._cmsToken },
    );

    return res.result.url;
  }
}

function defaultLocale(): string {
  if (typeof navigator !== 'undefined' && navigator.language) return navigator.language;
  return 'en-US';
}

function defaultUserAgent(): string {
  if (typeof navigator !== 'undefined' && navigator.userAgent) return navigator.userAgent;
  return 'tagadapay-headless-sdk';
}
